import { useRef, useState } from "react";

import { useSession } from "../../session/SessionContext.js";

/**
 * Choosing a photo, for an agent or for the assistant.
 *
 * ## Why the browser resizes it
 *
 * The server refuses anything over half a megabyte, and a photo straight off a
 * phone is several. Resizing here rather than there is not laziness: doing it
 * server-side would mean a native image library on a two-core box that also runs
 * the live API, for an operation the browser already does on the GPU in a few
 * milliseconds. The server still enforces the ceiling and still reads the format
 * from the bytes, so a client that skips this is refused rather than trusted.
 *
 * The output is always JPEG at a fixed box. An avatar is drawn at 40px and never
 * larger, so anything past 256 is bytes nobody sees.
 */

/** The longest edge, after resizing. */
const MAX_EDGE = 256;

/** Enough for a face at this size; past it the file grows and the picture does not. */
const JPEG_QUALITY = 0.82;

const ACCEPTED = "image/jpeg,image/png,image/webp";

async function downscale(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);

  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");

    if (context === null) throw new Error("This browser cannot resize images.");

    /*
      Filled white before drawing. A PNG with transparency becomes JPEG, which has
      no alpha channel — without this the transparent parts come out black, which
      on a head-and-shoulders cut-out is a striking way to ruin somebody's photo.
    */
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob === null) reject(new Error("That image could not be processed."));
          else resolve(blob);
        },
        "image/jpeg",
        JPEG_QUALITY,
      );
    });
  } finally {
    // Frees the decoded bitmap immediately rather than at the next collection —
    // a few megabytes per photo, on a tool people leave open all day.
    bitmap.close();
  }
}

export interface AvatarFieldProps {
  /** Used for the initials shown when there is no photo. */
  readonly name: string;
  /** `| undefined` explicitly: `exactOptionalPropertyTypes` distinguishes it from absent. */
  readonly avatarUrl?: string | undefined;
  /** Where the bytes go, e.g. `/support/profile/avatar`. */
  readonly uploadPath: string;
  /** Defaults to the upload path, which is the convention on both endpoints. */
  readonly deletePath?: string | undefined;
  readonly onChanged: (updated: unknown) => void;
}

function initialsOf(name: string): string {
  const words = name.split(/\s+/u).filter((word) => word !== "");

  return (
    words
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join("") || "·"
  );
}

export function AvatarField({
  name,
  avatarUrl,
  uploadPath,
  deletePath,
  onChanged,
}: AvatarFieldProps) {
  const { api } = useSession();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function choose(file: File | undefined): Promise<void> {
    if (file === undefined) return;

    setError(null);
    setBusy(true);

    try {
      const resized = await downscale(file);
      onChanged(await api.putBinary(uploadPath, resized, "image/jpeg"));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That photo could not be uploaded.");
    } finally {
      setBusy(false);
      // Cleared so choosing the same file twice in a row still fires a change.
      if (fileRef.current !== null) fileRef.current.value = "";
    }
  }

  async function remove(): Promise<void> {
    setError(null);
    setBusy(true);

    try {
      onChanged(await api.delete(deletePath ?? uploadPath));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That photo could not be removed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="avatar-field">
      {avatarUrl === undefined ? (
        <span className="avatar-preview" aria-hidden="true">
          {initialsOf(name)}
        </span>
      ) : (
        <img className="avatar-preview" src={avatarUrl} alt="" />
      )}

      <div className="avatar-actions">
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED}
          style={{ display: "none" }}
          onChange={(event) => {
            void choose(event.target.files?.[0]);
          }}
        />

        <div className="row">
          <button
            disabled={busy}
            onClick={() => {
              fileRef.current?.click();
            }}
          >
            {busy ? "Working…" : avatarUrl === undefined ? "Upload a photo" : "Replace"}
          </button>

          {avatarUrl !== undefined && (
            <button className="danger" disabled={busy} onClick={() => void remove()}>
              Remove
            </button>
          )}
        </div>

        <span className="hint">JPEG, PNG or WebP. Resized here before it is sent.</span>
        {error !== null && <p className="error">{error}</p>}
      </div>
    </div>
  );
}
